import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Theme,
  Grid,
  IconButton,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { ReminderConfig } from './ReminderConfigTable';
import { SaveReminderConfigRequest } from './reminder-config-api';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      backgroundColor: theme.color.grey.neutralBrand50,
      borderBottom: `1px solid ${theme.color.grey.border}`,
      padding: '16px 24px',
    },
    titleText: {
      fontSize: '18px',
      fontWeight: 600,
      color: theme.color.grey.neutralBrand900,
    },
    dialogContent: {
      padding: '24px',
      minHeight: '400px',
    },
    formSection: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.color.grey.neutralBrand800,
      marginBottom: '12px',
    },
    textField: {
      marginBottom: '16px',
    },
    customContentField: {
      '& .MuiInputBase-root': {
        minHeight: '100px',
        alignItems: 'flex-start',
      },
      '& .MuiInputBase-input': {
        padding: '12px',
      },
    },
    switchContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 0',
    },
    switchLabel: {
      margin: 0,
    },
    helperText: {
      fontSize: '12px',
      color: theme.color.grey.neutralBrand500,
      marginTop: '4px',
    },
    dialogActions: {
      padding: '16px 24px',
      backgroundColor: theme.color.grey.neutralBrand50,
      borderTop: `1px solid ${theme.color.grey.border}`,
    },
    saveButton: {
      minWidth: '100px',
    },
    cancelButton: {
      marginRight: '12px',
    },
  })
);

interface ReminderConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: SaveReminderConfigRequest) => void;
  editingConfig?: ReminderConfig | null;
  loading?: boolean;
}

const REMINDER_TYPES = [
  { value: 'published-roster', label: 'Published Roster Reminder' },
  { value: 'task-expiry-before', label: 'Task Expiry Reminder Before Expiry' },
  { value: 'task-expiry-after', label: 'Task Expiry Reminder After Expiry' },
  { value: 'birthday-reminder', label: 'Birthday Message' },
  { value: 'anniversary-reminder', label: 'Anniversary Message' },
];

const ReminderConfigModal: React.FC<ReminderConfigModalProps> = ({
  open,
  onClose,
  onSave,
  editingConfig,
  loading = false,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const [formData, setFormData] = useState({
    reminderType: 'published-roster' as
      | 'published-roster'
      | 'task-expiry-before'
      | 'task-expiry-after'
      | 'birthday-reminder'
      | 'anniversary-reminder',
    daysBefore: 1,
    customContent: '',
    isEnabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingConfig) {
      setFormData({
        reminderType: editingConfig.reminderType,
        daysBefore: editingConfig.daysBefore,
        customContent: editingConfig.customContent || '',
        isEnabled: editingConfig.isEnabled,
      });
    } else {
      setFormData({
        reminderType: 'published-roster',
        daysBefore: 1,
        customContent: '',
        isEnabled: true,
      });
    }
    setErrors({});
  }, [editingConfig, open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Birthday and anniversary reminders can be 0 days, others must be 1-60
    const isBirthdayOrAnniversary =
      formData.reminderType === 'birthday-reminder' || formData.reminderType === 'anniversary-reminder';
    const minDays = isBirthdayOrAnniversary ? 0 : 1;

    if (formData.daysBefore < minDays || formData.daysBefore > 60) {
      newErrors.daysBefore = isBirthdayOrAnniversary
        ? 'Days before must be between 0 and 60'
        : 'Days before must be between 1 and 60';
    }

    if (formData.customContent && formData.customContent.length > 1000) {
      newErrors.customContent = 'Custom message must be less than 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const configData = {
      ...formData,
      customContent: formData.customContent.trim() || null,
    };

    if (editingConfig) {
      onSave({
        ...configData,
        id: editingConfig.tenantReminderConfigId,
      });
    } else {
      onSave(configData);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: value,
      };

      // If reminder type changed to birthday or anniversary, set default days to 0
      if (field === 'reminderType' && (value === 'birthday-reminder' || value === 'anniversary-reminder')) {
        newFormData.daysBefore = 0;
      }
      // If changing from birthday/anniversary to other types, ensure minimum is 1
      else if (
        field === 'reminderType' &&
        value !== 'birthday-reminder' &&
        value !== 'anniversary-reminder' &&
        prev.daysBefore === 0
      ) {
        newFormData.daysBefore = 1;
      }

      return newFormData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const getModalTitle = () => {
    return editingConfig ? 'Edit' : 'Add New';
  };

  const getReminderTypeDescription = (type: string) => {
    switch (type) {
      case 'published-roster':
        return 'Automatically send reminder notifications to volunteers before their scheduled activities and shifts.';
      case 'task-expiry-before':
        return 'Automatically send reminder notifications to volunteers before task deadlines expire.';
      case 'task-expiry-after':
        return 'Automatically send follow-up notifications to volunteers after task deadlines have passed.';
      case 'birthday-reminder':
        return 'Automatically send birthday greetings to volunteers on or around their birthday.';
      case 'anniversary-reminder':
        return 'Automatically send anniversary congratulations to volunteers on or around their volunteer anniversary.';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between'>
          <Grid item>
            <Box alignItems='center'>
              <Typography
                style={{
                  fontSize: '23px',
                  fontWeight: 550,
                  marginRight: '16px',
                  color: theme.color.grey.neutral400,
                  paddingLeft: '5px',
                }}
              >
                {getModalTitle()}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display='flex' alignItems='center'>
              <IconButton key='close' aria-label='Close' color='inherit' onClick={onClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        <Box pr={3} mt={1}>
          <Typography style={{ fontSize: 14, paddingLeft: '5px', paddingRight: '10px' }} color='textSecondary'>
            Configure automated communication rules for your volunteers and activities.
            <br /> Hit &apos;Cancel&apos; to close this window.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '24px' }}>
        <Box display='flex' flexDirection='column' style={{ gap: '24px' }}>
          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Automation Type
            </Typography>
            <TextField
              select
              fullWidth
              value={formData.reminderType}
              onChange={(e) => handleFieldChange('reminderType', e.target.value)}
              variant='outlined'
              className={classes.textField}
              disabled={loading}
            >
              {REMINDER_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography style={{ fontSize: '13px', color: theme.color.grey.neutral300, marginTop: '8px' }}>
              {getReminderTypeDescription(formData.reminderType)}
            </Typography>
            {(formData.reminderType === 'birthday-reminder' || formData.reminderType === 'anniversary-reminder') && (
              <Box style={{ marginTop: '12px' }}>
                <AlertCard variant='info' title='Recommendation'>
                  <AlertCardBody>
                    <Typography
                      variant='body2'
                      style={{ fontSize: '13px', color: getAlertCardColors('info').lightTextColor }}
                    >
                      For {formData.reminderType === 'birthday-reminder' ? 'birthdays' : 'anniversaries'}, we recommend
                      setting this to 0 days as this is when the event takes place.
                    </Typography>
                  </AlertCardBody>
                </AlertCard>
              </Box>
            )}
            {(formData.reminderType === 'task-expiry-before' ||
              formData.reminderType === 'task-expiry-after' ||
              formData.reminderType === 'birthday-reminder' ||
              formData.reminderType === 'anniversary-reminder') && (
              <Box style={{ marginTop: '12px' }}>
                <AlertCard variant='info' title='Template Customisation'>
                  <AlertCardBody>
                    <Typography
                      variant='body2'
                      style={{ fontSize: '13px', color: getAlertCardColors('info').lightTextColor }}
                    >
                      You can customise the message template for{' '}
                      {formData.reminderType === 'task-expiry-before' || formData.reminderType === 'task-expiry-after'
                        ? 'task reminders'
                        : formData.reminderType === 'birthday-reminder'
                        ? 'birthday messages'
                        : 'anniversary messages'}{' '}
                      in <strong>Templates</strong> under the Communications section of the Admin Console.
                    </Typography>
                  </AlertCardBody>
                </AlertCard>
              </Box>
            )}
          </Box>

          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Days {formData.reminderType === 'task-expiry-after' ? 'After' : 'Before'}
            </Typography>
            <TextField
              type='number'
              fullWidth
              value={formData.daysBefore}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                const isBirthdayOrAnniversary =
                  formData.reminderType === 'birthday-reminder' || formData.reminderType === 'anniversary-reminder';
                const defaultValue = isBirthdayOrAnniversary ? 0 : 1;
                handleFieldChange('daysBefore', Number.isNaN(value) ? defaultValue : value);
              }}
              variant='outlined'
              label='Number of days'
              disabled={loading}
              error={!!errors.daysBefore}
              helperText={
                errors.daysBefore ||
                (() => {
                  const isBirthdayOrAnniversary =
                    formData.reminderType === 'birthday-reminder' || formData.reminderType === 'anniversary-reminder';
                  const range = isBirthdayOrAnniversary ? '0-60' : '1-60';
                  const timing = formData.reminderType === 'task-expiry-after' ? 'after' : 'before';
                  return `${range} days ${timing}`;
                })()
              }
              inputProps={{
                min:
                  formData.reminderType === 'birthday-reminder' || formData.reminderType === 'anniversary-reminder'
                    ? 0
                    : 1,
                max: 60,
              }}
            />
          </Box>

          <Box display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '16px 0' }}>
            <Box>
              <Typography style={{ fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400 }}>
                Enable Automation
              </Typography>
              <Typography style={{ fontSize: '13px', color: theme.color.grey.neutral300 }}>
                When enabled, this automation will send communications automatically based on your configuration.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEnabled}
                  onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                  color='primary'
                  disabled={loading}
                />
              }
              label=''
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box padding={2}>
          <TabletButton variant='text' color='error' onClick={onClose} disabled={loading} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : editingConfig ? 'Update Reminder' : 'Add Reminder'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { ReminderConfigModal };
